import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AgentsAPI } from "../api/agents";
import type { Agent } from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";

export const agentKeys = {
  list: (clientId: string) => ["agents", "list", clientId] as const,
};

export function useAgents() {
  const { clientId, scope } = useScopedClient();

  return useQuery({
    queryKey:
      scope === "staff"
        ? [...agentKeys.list(clientId), scope]
        : agentKeys.list(clientId),
    queryFn: async () => {
      // On the client's file in the platform back-office the staff mount only
      // answers names, which is all the screens mounted there (the calls) read.
      if (scope === "staff") {
        const data = await AgentsAPI.listNames(clientId);
        return data.agents as Agent[];
      }
      const data = await AgentsAPI.list(clientId);
      return data.agents;
    },
    enabled: !!clientId,
  });
}

export function useCreateAgent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agent: Partial<Agent>) => AgentsAPI.create(clientId, agent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.list(clientId) });
    },
  });
}

export function useUpdateAgent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, agent }: { id: string; agent: Partial<Agent> }) =>
      AgentsAPI.update(clientId, id, agent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.list(clientId) });
    },
  });
}

export function useDeleteAgent() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => AgentsAPI.delete(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.list(clientId) });
    },
  });
}
